import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  CaretLeft,
  CaretRight,
  MagnifyingGlassPlus,
  MagnifyingGlassMinus,
  ArrowsCounterClockwise,
} from "@phosphor-icons/react";

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// full-screen gallery for a set of photos, with zoom and pan
export const PortalLightbox = ({
  shots,
  index,
  srcOf,
  onClose,
  prefix = "portal-lightbox",
}) => {
  const [i, setI] = useState(index);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const step = useCallback(
    (d) => setI((v) => (v + d + shots.length) % shots.length),
    [shots.length],
  );

  useEffect(() => setI(index), [index]);
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [i]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
      else if (e.key === "+" || e.key === "=")
        setZoom((z) => clamp(z * 1.25, 1, 6));
      else if (e.key === "-") setZoom((z) => clamp(z / 1.25, 1, 6));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, onClose]);

  const shot = shots[i];
  if (!shot) return null;
  const key = shot.path || shot.photo?.id || String(i);

  const btn =
    "flex h-11 w-11 items-center justify-center rounded-full border border-zinc-700 bg-zinc-900/80 text-zinc-300 backdrop-blur transition-colors duration-150 hover:border-zinc-500 hover:text-white";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-zinc-950/95 backdrop-blur-sm"
      data-testid={prefix}
    >
      <div className="flex items-center gap-3 px-5 py-4">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-zinc-100">
            {shot.title}
          </div>
          <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            {shot.sub}
          </div>
        </div>
        <span
          className="font-mono-data ml-auto text-xs text-zinc-500"
          data-testid={`${prefix}-counter`}
        >
          {i + 1} / {shots.length}
        </span>
        <button
          type="button"
          data-testid={`${prefix}-zoom-out`}
          onClick={() => setZoom((z) => clamp(z / 1.25, 1, 6))}
          className={btn}
          title="Zoom out"
        >
          <MagnifyingGlassMinus size={17} weight="bold" />
        </button>
        <span
          className="font-mono-data w-12 text-center text-xs text-zinc-400"
          data-testid={`${prefix}-zoom-level`}
        >
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          data-testid={`${prefix}-zoom-in`}
          onClick={() => setZoom((z) => clamp(z * 1.25, 1, 6))}
          className={btn}
          title="Zoom in"
        >
          <MagnifyingGlassPlus size={17} weight="bold" />
        </button>
        <button
          type="button"
          data-testid={`${prefix}-zoom-reset`}
          onClick={() => {
            setZoom(1);
            setPan({ x: 0, y: 0 });
          }}
          className={btn}
          title="Fit to screen"
        >
          <ArrowsCounterClockwise size={16} weight="bold" />
        </button>
        <button
          type="button"
          data-testid={`${prefix}-close`}
          onClick={onClose}
          className={btn}
          title="Close"
        >
          <X size={18} weight="bold" />
        </button>
      </div>

      <div
        className="relative flex-1 overflow-hidden"
        onWheel={(e) =>
          setZoom((z) => clamp(z * (e.deltaY < 0 ? 1.12 : 1 / 1.12), 1, 6))
        }
        onPointerDown={(e) => {
          if (zoom === 1) return;
          drag.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
        }}
        onPointerMove={(e) => {
          if (!drag.current) return;
          setPan({
            x: e.clientX - drag.current.x,
            y: e.clientY - drag.current.y,
          });
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerLeave={() => {
          drag.current = null;
        }}
      >
        <img
          key={key}
          src={srcOf(shot.path, shot)}
          alt={shot.title}
          data-testid={`${prefix}-image`}
          onDoubleClick={() => setZoom((z) => (z > 1 ? 1 : 2.5))}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            cursor: zoom > 1 ? "grab" : "zoom-in",
          }}
          className="absolute inset-0 h-full w-full select-none object-contain p-6 transition-transform duration-150"
          draggable={false}
        />
        {shots.length > 1 && (
          <>
            <button
              type="button"
              data-testid={`${prefix}-prev`}
              onClick={() => step(-1)}
              className={`${btn} absolute left-4 top-1/2 -translate-y-1/2`}
              title="Previous photo"
            >
              <CaretLeft size={18} weight="bold" />
            </button>
            <button
              type="button"
              data-testid={`${prefix}-next`}
              onClick={() => step(1)}
              className={`${btn} absolute right-4 top-1/2 -translate-y-1/2`}
              title="Next photo"
            >
              <CaretRight size={18} weight="bold" />
            </button>
          </>
        )}
      </div>

      {shots.length > 1 && (
        <div
          className="flex gap-2 overflow-x-auto border-t border-zinc-800 px-5 py-3"
          data-testid={`${prefix}-strip`}
        >
          {shots.map((s, n) => (
            <button
              key={`${s.path || s.photo?.id || n}-${n}`}
              type="button"
              data-testid={`${prefix}-thumb-${n}`}
              onClick={() => setI(n)}
              className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border transition-colors duration-150 ${
                n === i
                  ? "border-indigo-400"
                  : "border-zinc-800 hover:border-zinc-600"
              }`}
            >
              <img
                src={srcOf(s.thumb || s.path, s)}
                alt={s.title}
                className={`h-full w-full object-cover ${n === i ? "" : "opacity-60"}`}
              />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
};
